import React, { useEffect } from 'react';
import styled from 'styled-components';
import { DraggableData } from 'react-draggable';
import { useDispatch } from 'react-redux';
import { Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import { GridCard, updateCard } from '../redux/cardDbSlice';
import { ArrowDb } from '../redux/arrowDbSlice';
import { CoordinateType } from '../general';
import { getCardComponent } from '../card_components';
import CardPad from './CardPad';
import Arrow from './Arrow';
import { CardMenuAction, ArrowMenuAction, OperationMode, ShowMenuType } from './BookEditor';

type SelectingBlankProps = {
  x: number;
  y: number;
}
const SelectingBlank = styled(Grid)<SelectingBlankProps>`
  position: absolute;
  width: 220px;
  height: 144px;
  border: 2px solid blue;
  left: ${props => props.x + 'px'};
  top: ${props => props.y + 'px'};
  color: #bbdefb;
  transition: 300ms;
  transition-property: background-color, color;
  :hover {
    background-color: #e3f2fd;
    color: #90caf9;
  }
`
const BlankAddIcon = styled(AddIcon)`
  font-size: 96px;
`

type GridSheetProps = {
  ids: string[];
  cards: GridCard[];
  arrowIds: string[];
  arrows: ArrowDb[];
  showMenu: ShowMenuType;
  setShowMenu: (value: ShowMenuType) => void;
  toggleArrowMenuData: (id?: string, arrow?: ArrowDb, ev?: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  selectedId: string | undefined;
  mode: OperationMode;
  clickRect: CoordinateType | undefined;
  selectingGrid: CoordinateType | undefined;
  setSelectingGrid: (grid?: CoordinateType) => void;
  cardMenuClicked: CardMenuAction;
  arrowMenuClicked: ArrowMenuAction;
  blankAddClicked: () => void;
  connectId: string | undefined;
  handleChangeConnectId: (id: string) => void;
  scaleCoefficient: number;
}
const GridSheet: React.FC<GridSheetProps> = ({ids, cards, arrowIds, arrows, showMenu, setShowMenu, toggleArrowMenuData, selectedId, mode, clickRect, selectingGrid, setSelectingGrid, cardMenuClicked, arrowMenuClicked, blankAddClicked, connectId, handleChangeConnectId, scaleCoefficient}) => {
  const dispatch = useDispatch();
  const colomnWidth = 220;
  const rowHeight = 144;

  useEffect(() => {
    if (clickRect) {
      const clickedColomn = Math.floor(clickRect.x / scaleCoefficient / colomnWidth);
      const clickedRow = Math.floor(clickRect.y / scaleCoefficient / rowHeight);
      let blankFlag = true;
      for (let i = 0; i < cards.length; i++) {
        if (clickedColomn === cards[i].column && clickedRow === cards[i].row) {
          blankFlag = false;
          break;
        }
      }
      if (blankFlag) setSelectingGrid({x: clickedColomn, y: clickedRow});
      else setSelectingGrid(undefined);
    }
  }, [clickRect])

  const changeGrid = (data: DraggableData, ix: number, id: string, card: GridCard) => {
    const newColomn = Math.round(data.x / colomnWidth);
    const newRow = Math.round(data.y / rowHeight);

    if (newColomn !== card.column || newRow !== card.row) {
      for (let i = 0; i < cards.length; i++) {
        if (cards[i].column === newColomn && cards[i].row === newRow) {
          dispatch(updateCard({id: ids[i], x: card.column * colomnWidth, y: card.row * rowHeight, column: card.column, row: card.row}));
        }
      }
      dispatch(updateCard({id: ids[ix], x: newColomn * colomnWidth, y: newRow * rowHeight, column: newColomn, row: newRow}));
    }
  }

  return (
    <>
      { ids.map((id, ix) => {
        const card = cards[ix]
        return (
          <CardPad key={id} id={id}
            cardMenuClicked={cardMenuClicked}
            showMenu={showMenu} setShowMenu={setShowMenu}
            scaleCoefficient={scaleCoefficient}
            mode={mode} connectId={connectId}
            handleChangeConnectId={handleChangeConnectId}
            changeGrid={(data: DraggableData) => changeGrid(data, ix, id, card)}
          >
            { getCardComponent(card.kind, id) }
          </CardPad>
        );
      }) }
      { arrowIds.map((id, ix) => {
        const arrow = arrows[ix];
        let sibLength = 1;
        arrowIds.forEach((targetId, targetIx) => {
          if (targetId === id) return;
          const target = arrows[targetIx];
          if ((target.fromId === arrow.fromId && target.toId === arrow.toId) || (target.fromId === arrow.toId && target.toId === arrow.fromId)) sibLength++;
        })
        return (
          <Arrow key={id} id={id} data={arrow} sibLength={sibLength}
            selected={id === selectedId}
            arrowMenuClicked={arrowMenuClicked}
            onContextMenu={(ev) => toggleArrowMenuData(id, arrow, ev)}
          />
        );
      }) }
      { selectingGrid &&
        <SelectingBlank container justifyContent="center" alignItems="center"
          x={selectingGrid.x * colomnWidth} y={selectingGrid.y * rowHeight}
          onClick={blankAddClicked}
        >
          <Grid item>
            <BlankAddIcon />
          </Grid>
        </SelectingBlank>
      }
    </>
  );
}

export default GridSheet;
